import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { formatINR } from "@/components/shared";
import { Printer, ArrowLeft } from "lucide-react";

// -------- Indian number-to-words --------------------------------------------
import { formatDate, formatDateTime } from "@/lib/datetime";
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function belowThousand(n) {
  if (n === 0) return "";
  if (n < 20) return ONES[n];
  if (n < 100) return TENS[Math.floor(n / 10)] + (n % 10 ? " " + ONES[n % 10] : "");
  return ONES[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + belowThousand(n % 100) : "");
}

function inrInWords(amount) {
  if (amount === null || amount === undefined) return "";
  const rounded = Math.round(amount * 100);
  const rupees = Math.floor(rounded / 100);
  const paise = rounded % 100;
  if (rupees === 0 && paise === 0) return "Rupees Zero Only";
  const parts = [];
  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const rest = rupees % 1000;
  if (crore) parts.push(belowThousand(crore) + " Crore");
  if (lakh) parts.push(belowThousand(lakh) + " Lakh");
  if (thousand) parts.push(belowThousand(thousand) + " Thousand");
  if (rest) parts.push(belowThousand(rest));
  let words = "Rupees " + parts.join(" ");
  if (paise) words += " and " + belowThousand(paise) + " Paise";
  return words + " Only";
}

export default function SalesInvoicePrint() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/sales/invoices/${id}/print`);
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, [id]);

  useEffect(() => { if (!loading && data) setTimeout(() => window.print(), 400); }, [loading, data]);

  if (loading) return <div className="p-8 text-gray-500">Loading…</div>;
  if (!data) return <div className="p-8 text-red-600">Invoice not found</div>;

  const inv = data.invoice;
  const biz = data.business || {};
  const cust = inv.customer_snapshot || {};
  const intra = inv.is_intrastate;
  const t = inv.totals || {};

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 p-3 flex items-center gap-3 shadow-sm">
        <button onClick={() => window.history.back()} className="h-9 w-9 rounded-md border border-gray-200 hover:bg-gray-50 flex items-center justify-center">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="font-heading font-semibold text-gray-900">Print Preview — {inv.invoice_no || "(Draft)"}</span>
        <button onClick={() => window.print()} className="ml-auto h-9 px-3 rounded-md bg-[#9B1C1C] hover:bg-[#7F1D1D] text-white text-sm font-semibold flex items-center gap-1.5" data-testid="inv-print-do">
          <Printer className="h-4 w-4" /> Print
        </button>
      </div>

      <div className="max-w-[210mm] mx-auto bg-white shadow-sm print:shadow-none p-8 my-6 print:my-0 print:p-0 text-gray-900">
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-[#9B1C1C] pb-4 mb-6">
          <div>
            {biz.logo_url && <img src={biz.logo_url} alt="Logo" className="h-14 mb-2 object-contain" />}
            <div className="font-heading font-bold text-2xl text-[#9B1C1C] leading-tight">{biz.business_name || "Thara Gold"}</div>
            {biz.legal_name && biz.legal_name !== biz.business_name && <div className="text-xs text-gray-600">{biz.legal_name}</div>}
            <div className="text-xs text-gray-700 mt-1 leading-tight">
              {[biz.address_line1, biz.address_line2].filter(Boolean).join(", ")}<br />
              {[biz.city, biz.district, biz.state, biz.pincode].filter(Boolean).join(", ")}<br />
              {biz.phone && <>Ph: {biz.phone}{biz.email ? ` · ${biz.email}` : ""}</>}
            </div>
            <div className="text-[11px] text-gray-700 font-mono mt-1">
              {biz.gstin && <>GSTIN: <strong>{biz.gstin}</strong></>}
              {biz.pan && <> · PAN: {biz.pan}</>}
            </div>
          </div>
          <div className="text-right">
            <div className="uppercase tracking-widest text-[11px] text-gray-500">Tax Invoice</div>
            <div className="font-heading font-bold text-xl text-gray-900 mt-1">{inv.invoice_no || "DRAFT"}</div>
            <div className="text-xs text-gray-600 mt-1">Date: <strong>{formatDate(inv.invoice_date)}</strong></div>
            {inv.due_date && <div className="text-xs text-gray-600">Due: {inv.due_date}</div>}
            {inv.reference && <div className="text-xs text-gray-600">Ref: {inv.reference}</div>}
          </div>
        </div>

        {/* Bill To */}
        <div className="grid grid-cols-2 gap-6 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Bill To</div>
            <div className="font-semibold text-sm">{cust.name}</div>
            <div className="text-xs text-gray-700 leading-tight">
              {cust.code && <span className="font-mono">{cust.code}</span>}<br />
              {[cust.address_line1, cust.address_line2].filter(Boolean).join(", ")}<br />
              {[cust.city, cust.district, cust.state, cust.pincode].filter(Boolean).join(", ")}<br />
              {cust.mobile && <>Ph: {cust.mobile}</>}
            </div>
            {cust.gstin && <div className="text-[11px] font-mono text-gray-700 mt-1">GSTIN: <strong>{cust.gstin}</strong></div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Details</div>
            <table className="text-xs text-gray-700 w-full">
              <tbody>
                <tr><td className="py-0.5 text-gray-500">Warehouse</td><td className="py-0.5">{inv.warehouse_name}</td></tr>
                <tr><td className="py-0.5 text-gray-500">Place of Supply</td><td className="py-0.5">{cust.state || "—"}</td></tr>
                <tr><td className="py-0.5 text-gray-500">Supply Type</td><td className="py-0.5">{intra ? "Intra-state (CGST+SGST)" : "Inter-state (IGST)"}</td></tr>
                {inv.payment_terms && <tr><td className="py-0.5 text-gray-500">Payment Terms</td><td className="py-0.5">{inv.payment_terms}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lines */}
        <table className="w-full text-xs mb-4 border border-gray-300">
          <thead className="bg-gray-100">
            <tr className="text-left border-b border-gray-300">
              <th className="px-2 py-2 w-6 text-center">#</th>
              <th className="px-2 py-2">Description</th>
              <th className="px-2 py-2 w-20 text-center">HSN</th>
              <th className="px-2 py-2 w-16 text-right">Qty</th>
              <th className="px-2 py-2 w-20 text-right">Rate</th>
              <th className="px-2 py-2 w-14 text-right">Disc%</th>
              <th className="px-2 py-2 w-20 text-right">Taxable</th>
              {intra ? (
                <>
                  <th className="px-2 py-2 w-16 text-right">CGST</th>
                  <th className="px-2 py-2 w-16 text-right">SGST</th>
                </>
              ) : (
                <th className="px-2 py-2 w-16 text-right">IGST</th>
              )}
              <th className="px-2 py-2 w-24 text-right">Line Total</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((ln, i) => (
              <tr key={i} className="border-b border-gray-200">
                <td className="px-2 py-1.5 text-center text-gray-500">{i + 1}</td>
                <td className="px-2 py-1.5">
                  <div className="font-medium">{ln.product_name}</div>
                  {ln.product_sku && <div className="text-[10px] text-gray-500 font-mono">{ln.product_sku}</div>}
                </td>
                <td className="px-2 py-1.5 text-center font-mono">{ln.hsn_code || "—"}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{ln.qty}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{formatINR(ln.rate)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{ln.discount_pct ? ln.discount_pct + "%" : "—"}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{formatINR(ln.taxable)}</td>
                {intra ? (
                  <>
                    <td className="px-2 py-1.5 text-right tabular-nums">{formatINR(ln.cgst)}<div className="text-[9px] text-gray-500">@{(ln.tax_pct / 2).toFixed(2)}%</div></td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{formatINR(ln.sgst)}<div className="text-[9px] text-gray-500">@{(ln.tax_pct / 2).toFixed(2)}%</div></td>
                  </>
                ) : (
                  <td className="px-2 py-1.5 text-right tabular-nums">{formatINR(ln.igst)}<div className="text-[9px] text-gray-500">@{ln.tax_pct}%</div></td>
                )}
                <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{formatINR(ln.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="grid grid-cols-2 gap-6">
          <div className="text-xs text-gray-700">
            <div className="mb-2">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Amount in Words</div>
              <div className="font-semibold text-gray-900">{inrInWords(t.grand_total)}</div>
            </div>
            {biz.invoice_terms && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 mt-3">Terms & Conditions</div>
                <div className="whitespace-pre-line text-[11px] leading-snug">{biz.invoice_terms}</div>
              </div>
            )}
            {(biz.bank_name || biz.bank_account_no) && (
              <div className="mt-3 border-t border-gray-200 pt-2">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Bank Details</div>
                <div className="text-[11px] leading-tight">
                  {biz.bank_name && <>{biz.bank_name}<br /></>}
                  {biz.bank_account_name && <>A/c Name: {biz.bank_account_name}<br /></>}
                  {biz.bank_account_no && <>A/c: <span className="font-mono">{biz.bank_account_no}</span><br /></>}
                  {biz.bank_ifsc && <>IFSC: <span className="font-mono">{biz.bank_ifsc}</span><br /></>}
                  {biz.bank_upi && <>UPI: <span className="font-mono">{biz.bank_upi}</span></>}
                </div>
              </div>
            )}
          </div>
          <div>
            <table className="w-full text-xs">
              <tbody>
                <tr><td className="py-1 text-gray-600">Subtotal</td><td className="py-1 text-right tabular-nums">{formatINR(t.subtotal)}</td></tr>
                {t.line_discount_total > 0 && <tr><td className="py-1 text-gray-600">Line Discounts</td><td className="py-1 text-right tabular-nums text-red-600">- {formatINR(t.line_discount_total)}</td></tr>}
                {t.invoice_discount > 0 && <tr><td className="py-1 text-gray-600">Invoice Discount</td><td className="py-1 text-right tabular-nums text-red-600">- {formatINR(t.invoice_discount)}</td></tr>}
                <tr className="border-t border-gray-200"><td className="py-1 text-gray-600">Taxable</td><td className="py-1 text-right tabular-nums font-semibold">{formatINR(t.taxable_amount)}</td></tr>
                {intra ? (
                  <>
                    <tr><td className="py-1 text-gray-600">CGST</td><td className="py-1 text-right tabular-nums">{formatINR(t.cgst_total)}</td></tr>
                    <tr><td className="py-1 text-gray-600">SGST</td><td className="py-1 text-right tabular-nums">{formatINR(t.sgst_total)}</td></tr>
                  </>
                ) : (
                  <tr><td className="py-1 text-gray-600">IGST</td><td className="py-1 text-right tabular-nums">{formatINR(t.igst_total)}</td></tr>
                )}
                {Math.abs(t.roundoff || 0) > 0.001 && <tr><td className="py-1 text-gray-500 italic">Round-off</td><td className="py-1 text-right tabular-nums text-gray-500">{formatINR(t.roundoff)}</td></tr>}
                <tr className="border-t-2 border-[#9B1C1C]">
                  <td className="py-2 font-heading font-bold text-sm">Grand Total</td>
                  <td className="py-2 text-right tabular-nums font-heading font-bold text-lg text-[#9B1C1C]">{formatINR(t.grand_total)}</td>
                </tr>
              </tbody>
            </table>
            <div className="mt-8 text-right">
              <div className="text-[10px] uppercase tracking-widest text-gray-500">For {biz.business_name || "Thara Gold"}</div>
              {biz.invoice_signature_url && <img src={biz.invoice_signature_url} alt="Signature" className="h-12 ml-auto my-2" />}
              <div className="border-t border-gray-400 mt-6 pt-1 text-xs font-semibold">Authorised Signatory</div>
            </div>
          </div>
        </div>

        {biz.invoice_footer_note && <div className="mt-6 text-center text-xs text-gray-500 italic border-t border-gray-200 pt-2">{biz.invoice_footer_note}</div>}
      </div>
    </div>
  );
}
